import type {
  GetPortalNavigationResponseType,
  MenuData,
  NavigationItem,
  PortalInstanceType,
  PortalNavigationEntry,
} from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/NavigationRpc';

const DYNAMIC_OBJECT_ENABLED_TYPES = new Set<PortalInstanceType>(['DEAL_PLAN']);

// A "space" is a user-facing portal instance. UNIVERSAL (My Account/Login) and
// HOME_APP (the home dashboard itself) are shell surfaces, not spaces, so they
// are excluded from the space count that drives home layout decisions.
const NON_SPACE_INSTANCE_TYPES = new Set<PortalInstanceType>([
  'UNIVERSAL',
  'HOME_APP',
]);

export type SideNavItemData = {
  id: string;
  label?: string;
  href?: string;
  path?: string;
  isCurrent?: boolean;
  isActive?: boolean;
  external?: boolean;
  badgeCount?: number;
  children: SideNavItemData[];
};

export type PortalSpace = {
  portalInstanceId: number;
  portalInstanceType: PortalInstanceType;
  displayName: string;
  isCurrent: boolean;
};

export type PortalNavigationData = {
  navItems: SideNavItemData[];
  siteMenuData?: MenuData;
  shouldShowSideNav: boolean;
  requestedInstanceDisplayName?: string;
  loading: boolean;
  spaces: PortalSpace[];
  spaceCount: number;
};

export type BuildPortalNavigationInput = {
  data: GetPortalNavigationResponseType | undefined;
  loading: boolean;
  currentPathname: string;
};

function hrefToPathname(href: string): string {
  return href.startsWith('/') ? href : new URL(`https://${href}`).pathname;
}

function isActiveRoute(href: string | undefined, currentPathname: string) {
  if (!href) return false;
  const hrefPath = hrefToPathname(href);
  return (
    currentPathname === hrefPath || currentPathname.startsWith(`${hrefPath}/`)
  );
}

function toSideNavItem(
  item: NavigationItem,
  hrefBase: string,
  isCurrent: boolean,
  currentPathname: string
): SideNavItemData {
  const href = item.path ? `${hrefBase}${item.path}` : undefined;
  const children = (item.children ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((child) => toSideNavItem(child, hrefBase, isCurrent, currentPathname));
  const isLeaf = children.length === 0;

  const isActive =
    isCurrent && (!isLeaf || !href || isActiveRoute(href, currentPathname));

  return {
    id: item.id,
    href,
    path: item.path,
    isCurrent,
    isActive,
    external: item.external,
    badgeCount: item.badge?.count,
    children,
  };
}

function getHrefBase(baseUrl: string): string {
  if (!baseUrl) return '';
  if (baseUrl.startsWith('/') && !baseUrl.startsWith('//')) {
    return baseUrl.replace(/\/$/, '');
  }
  return new URL(`https://${baseUrl.replace(/^\/\//, '')}`).pathname.replace(
    /\/$/,
    ''
  );
}

function isDynamicObjectEnabledType(type: PortalInstanceType): boolean {
  return DYNAMIC_OBJECT_ENABLED_TYPES.has(type);
}

function hasDynamicObjects(entry: PortalNavigationEntry): boolean {
  return (
    isDynamicObjectEnabledType(entry.portalInstanceType) &&
    (entry.dynamicPortalObjects ?? []).length > 0
  );
}

function applyDynamicPortalObjects(
  navItem: SideNavItemData,
  entry: PortalNavigationEntry,
  hrefBase: string
): SideNavItemData {
  const dynamicObjects = entry.dynamicPortalObjects ?? [];

  if (
    !isDynamicObjectEnabledType(entry.portalInstanceType) ||
    dynamicObjects.length === 0
  ) {
    return navItem;
  }

  if (dynamicObjects.length === 1) {
    return {
      ...navItem,
      href: `${hrefBase}/${dynamicObjects[0].objectId}`,
      children: [],
    };
  }

  const dynamicChildren: SideNavItemData[] = dynamicObjects.map((obj) => ({
    id: `${navItem.id}.${obj.objectId}`,
    label: obj.displayName,
    href: `${hrefBase}/${obj.objectId}`,
    isCurrent: navItem.isCurrent,
    isActive: false,
    children: [],
  }));

  return {
    ...navItem,
    children: dynamicChildren,
  };
}

function countDestinations(items: NavigationItem[]): number {
  return items.reduce((count, item) => {
    const children = item.children ?? [];
    return count + (children.length > 0 ? children.length : 1);
  }, 0);
}

export function buildPortalNavigation({
  data,
  loading,
  currentPathname,
}: BuildPortalNavigationInput): PortalNavigationData {
  const entries = data?.portalNavigationEntries ?? [];
  const siteMenuData = data?.siteMenuData;
  const siteMenuItemCount = siteMenuData?.items?.length ?? 0;
  const nonUniversalEntries = entries.filter(
    (e) => e.portalInstanceType !== 'UNIVERSAL'
  );
  const shouldPromoteChildren =
    nonUniversalEntries.length <= 1 && siteMenuItemCount === 0;

  const navItems = nonUniversalEntries.flatMap((entry) => {
    if (!entry.navigationManifest?.items) return [];
    const hrefBase = getHrefBase(entry.baseUrl);
    const entryHasDynamicObjects = hasDynamicObjects(entry);
    return entry.navigationManifest.items
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .flatMap((item) => {
        const baseNavItem = toSideNavItem(
          item,
          hrefBase,
          entry.isCurrent,
          currentPathname
        );
        const navItem = applyDynamicPortalObjects(baseNavItem, entry, hrefBase);
        if (
          shouldPromoteChildren &&
          !entryHasDynamicObjects &&
          navItem.children.length > 0
        ) {
          return navItem.children;
        }
        return [navItem];
      });
  });

  const nonUniversalDestinationCount = nonUniversalEntries.reduce(
    (count, entry) => {
      const manifestCount = countDestinations(
        entry.navigationManifest?.items ?? []
      );
      const dynamicCount = hasDynamicObjects(entry)
        ? (entry.dynamicPortalObjects ?? []).length
        : 0;
      return count + Math.max(manifestCount, dynamicCount);
    },
    0
  );
  const shouldShowSideNav =
    loading || nonUniversalDestinationCount > 1 || siteMenuItemCount > 0;

  const spaces: PortalSpace[] = entries
    .filter((e) => !NON_SPACE_INSTANCE_TYPES.has(e.portalInstanceType))
    .map((e) => ({
      portalInstanceId: e.portalInstanceId,
      portalInstanceType: e.portalInstanceType,
      displayName: e.displayName,
      isCurrent: e.isCurrent,
    }));

  return {
    navItems,
    siteMenuData,
    shouldShowSideNav,
    requestedInstanceDisplayName: data?.requestedInstanceDisplayName,
    loading,
    spaces,
    spaceCount: spaces.length,
  };
}
